import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Lock, Globe, ArrowRight, Check, ImagePlus, X, UserPlus, Mail, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { compressImageToDataUrl } from "../utils/imageCompress";

const API = "/api";

const PROMPTS_SELF = [
  "A small thing most people wouldn't notice…",
  "Someone who made this week lighter…",
  "Something my body did for me today…",
];

const PROMPTS_OFFERED = [
  "One thing you'd want them to know about themselves…",
  "Something they do that makes life better for you…",
];

const PROMPTS_ASK = [
  "What's something you think I undervalue about myself?",
  "What's one thing about me that makes your life better?",
  "Where do you see me clearly that I might not?",
];

const MAX_IMAGES = 4;

const AddGratitudeDialog = ({
  open,
  onClose,
  mode = "self",
  onCreated,
  connections = [],
  initialConnectionId = null,
  initialRequestId = null,
  initialRequester = null,
}) => {
  const [step, setStep] = useState(mode === "offered" || mode === "ask" ? "pickPerson" : "compose");
  const [selectedConn, setSelectedConn] = useState(null);            // ask: single conn
  // Offered mode: multi-select. Each entry is either a connection object
  // (with connection_id) or a synthetic pending-invite object
  // { _isPending: true, display_name, email }. Asks still use `selectedConn`.
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [content, setContent] = useState("");
  const [prompt, setPrompt] = useState("");
  const [wheelAreaKeys, setWheelAreaKeys] = useState([]);
  const [wheelAreas, setWheelAreas] = useState([]);
  // Tags are free-form #-style labels (e.g. "food", "uruguay"). Normalized
  // to plain strings client-side before send.
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState("");
  // Multi-image support: always a list. `imageDataUrl` kept for ask mode only.
  const [images, setImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);
  // For "someone not on Evoluras yet"
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [visibility, setVisibility] = useState(mode === "self" ? "private" : "public");
  const [submitting, setSubmitting] = useState(false);
  const [randomPrompt, setRandomPrompt] = useState("");

  useEffect(() => {
    if (!open) return;
    setContent("");
    setPrompt("");
    setSelectedConn(null);
    setSelectedRecipients([]);
    setSubmitting(false);
    setWheelAreaKeys([]);
    setTags([]);
    setTagDraft("");
    setImages([]);
    setImageUploading(false);
    setInviteName("");
    setInviteEmail("");
    setVisibility(mode === "self" ? "private" : "public");

    if (initialConnectionId) {
      const c = connections.find(cn => cn.connection_id === initialConnectionId);
      if (c) {
        setSelectedConn(c);
        // For offered mode with initial connection (e.g. from a card's Reply
        // button), pre-select them and skip straight to compose.
        if (mode === "offered") setSelectedRecipients([c]);
        setStep("compose");
      }
    } else if (mode === "offered" || mode === "ask") {
      setStep("pickPerson");
    } else {
      setStep("compose");
    }

    const pool = mode === "ask" ? PROMPTS_ASK : (mode === "offered" ? PROMPTS_OFFERED : PROMPTS_SELF);
    setRandomPrompt(pool[Math.floor(Math.random() * pool.length)]);

    // Load wheel areas for the area-picker chips
    if (mode !== "ask") {
      (async () => {
        try {
          const res = await fetch(`${API}/tools/wheel`, { credentials: "include" });
          if (!res.ok) return;
          const data = await res.json();
          const active = (data.wheel?.areas && data.wheel.areas.length > 0)
            ? data.wheel.areas
            : (data.default_areas || []);
          setWheelAreas(active);
        } catch {
          /* non-blocking */
        }
      })();
    }
  }, [open, mode, initialConnectionId, connections]);

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (images.length >= MAX_IMAGES) {
      toast.error(`Up to ${MAX_IMAGES} images per card`);
      return;
    }
    // Reject large raw files early (>10MB) — they'll still compress but UX is slow
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is too large. Please pick one under 10 MB.");
      return;
    }
    setImageUploading(true);
    try {
      const dataUrl = await compressImageToDataUrl(file, {
        maxDim: 1200,
        maxBytes: 900_000,
        mimeType: file.type === "image/png" ? "image/png" : "image/jpeg",
      });
      setImages(prev => [...prev, dataUrl]);
    } catch {
      toast.error("Couldn't read that image");
    } finally {
      setImageUploading(false);
    }
  };

  const removeImageAt = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (mode === "ask") {
        const isPending = selectedConn?._isPending;
        const url = isPending ? `${API}/gratitude/ask-invite` : `${API}/gratitude/ask`;
        const body = isPending
          ? {
              name: selectedConn.display_name,
              email: selectedConn.email,
              prompt: prompt.trim() || null,
            }
          : {
              connection_id: selectedConn.connection_id,
              prompt: prompt.trim() || null,
            };
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to send");
        }
        toast.success(`Your ask was sent to ${(selectedConn.display_name || "").split(" ")[0]}`);
        onCreated?.();
        onClose();
        return;
      }

      if (mode === "offered") {
        // Split recipients into existing-connections and pending-invites.
        // Each group hits its own endpoint in parallel; both support multi-
        // recipient payloads now (connection_ids / recipients).
        const connRecipients = selectedRecipients.filter(r => !r._isPending && r.connection_id);
        const inviteRecipients = selectedRecipients.filter(r => r._isPending);

        if (connRecipients.length === 0 && inviteRecipients.length === 0) {
          toast.error("Pick at least one person");
          setSubmitting(false);
          return;
        }

        // Thank-back only makes sense when sending to a single existing
        // connection — bail if the user tried to mix.
        if (initialRequestId && (connRecipients.length !== 1 || inviteRecipients.length !== 0)) {
          toast.error("Replies to an ask can only be sent to one person");
          setSubmitting(false);
          return;
        }

        const commonBody = {
          content: content.trim(),
          wheel_area_keys: wheelAreaKeys,
          tags,
          image_data_urls: images,  // backend also accepts image_data_url for legacy
        };
        const calls = [];
        if (connRecipients.length > 0) {
          calls.push(fetch(`${API}/gratitude/offer`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...commonBody,
              connection_ids: connRecipients.map(c => c.connection_id),
              request_id: initialRequestId || null,
            }),
          }));
        }
        if (inviteRecipients.length > 0) {
          calls.push(fetch(`${API}/gratitude/offer-invite`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...commonBody,
              recipients: inviteRecipients.map(r => ({ name: r.display_name, email: r.email })),
            }),
          }));
        }
        const results = await Promise.all(calls);
        for (const res of results) {
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || "Failed to send");
          }
        }
        const total = connRecipients.length + inviteRecipients.length;
        if (total === 1) {
          const name = (selectedRecipients[0].display_name || "").split(" ")[0];
          toast.success(selectedRecipients[0]._isPending
            ? `Invite + gratitude sent to ${name}`
            : `Sent to ${name}`);
        } else {
          toast.success(`Gratitude sent to ${total} people`);
        }
        onCreated?.();
        onClose();
        return;
      }

      const res = await fetch(`${API}/gratitude/cards`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: content.trim(),
          wheel_area_keys: wheelAreaKeys,
          tags,
          image_data_urls: images,
          visibility,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to save");
      }
      // Gentle nudge if lingua detected a different language than declared.
      // This is advisory only — the card was already saved with the user's
      // chosen lang. They can re-write or ignore.
      const saved = await res.json().catch(() => ({}));
      if (saved?.lang_warn_mismatch) {
        toast.message("Saved. Heads-up: this looks like it's in a different language from your profile setting.");
      } else {
        toast.success("Added to your wall");
      }
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const title = {
    self: "Add something you are grateful for:",
    offered: selectedConn
      ? `Send your gratitude to ${(selectedConn.display_name || "").split(" ")[0]}`
      : "Send your gratitude to:",
    ask: "Ask someone what they are grateful for about you",
  }[mode];

  const acceptedConnections = connections.filter(c => c.status === "accepted" && c.display_name);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden flex flex-col"
        style={{ maxHeight: "90vh" }}
        data-testid="gratitude-dialog"
      >
        <div className="px-6 pt-5 pb-2 flex-shrink-0">
          <DialogTitle
            className="text-lg font-semibold"
            style={{ color: "var(--color-text-primary)" }}
            data-testid="gratitude-dialog-title"
          >
            {title}
          </DialogTitle>
          {initialRequester && (
            <p className="text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>
              Responding to {initialRequester}&apos;s ask
            </p>
          )}
        </div>

        {/* Scrollable content region — prevents the footer from being pushed
            off-screen when the compose step has multiple image previews or a
            long textarea. `flex-1 overflow-y-auto` caps it to the dialog's
            max-height and lets everything above the footer scroll. */}
        <div className="px-6 pb-5 space-y-4 flex-1 overflow-y-auto min-h-0">
          {step === "pickPerson" && (
            <div className="space-y-3">
              <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                {mode === "ask"
                  ? "Who would you like to ask?"
                  : (selectedRecipients.length > 0
                      ? `Pick everyone you want to thank — selected ${selectedRecipients.length}`
                      : "Who would you like to write to? (you can pick several)")}
              </p>
              {acceptedConnections.length === 0 && selectedRecipients.length === 0 ? (
                <div
                  className="p-6 rounded-lg text-center text-sm"
                  style={{
                    background: "var(--color-surface-subtle)",
                    border: "1px dashed var(--color-border-default)",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  You need at least one accepted connection in your Circle to {mode === "ask" ? "ask for gratitude" : "offer gratitude"}.
                </div>
              ) : acceptedConnections.length > 0 ? (
                <div
                  className="max-h-64 overflow-y-auto rounded-lg"
                  style={{
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border-default)",
                  }}
                  data-testid="connection-picker"
                >
                  {acceptedConnections.map(c => {
                    const isSelected = mode === "offered"
                      ? selectedRecipients.some(r => r.connection_id === c.connection_id)
                      : false;
                    const toggle = () => {
                      if (mode === "ask") {
                        setSelectedConn(c);
                        setStep("compose");
                        return;
                      }
                      setSelectedRecipients(prev =>
                        isSelected
                          ? prev.filter(r => r.connection_id !== c.connection_id)
                          : [...prev, c]
                      );
                    };
                    return (
                    <button
                      key={c.connection_id}
                      onClick={toggle}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                      style={{
                        borderBottom: "0.5px solid var(--color-border-default)",
                        background: isSelected ? "var(--color-brand-ghost)" : "transparent",
                      }}
                      data-testid={`connection-pick-${c.connection_id}`}
                      aria-pressed={isSelected}
                    >
                      {c.display_picture ? (
                        <img src={c.display_picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center"
                          style={{
                            background: "var(--color-brand-ghost)",
                            color: "var(--color-brand-primary)",
                            fontWeight: 600,
                          }}
                        >
                          {(c.display_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: "var(--color-text-primary)" }}>
                          {c.display_name}
                        </div>
                        <div className="text-xs truncate" style={{ color: "var(--color-text-secondary)" }}>
                          {(c.display_role || c.role || "").replace(/_/g, " ")}
                        </div>
                      </div>
                      {mode === "offered" ? (
                        isSelected ? (
                          <Check size={16} style={{ color: "var(--color-brand-primary)" }} />
                        ) : (
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ border: "1.5px solid var(--color-border-default)" }}
                          />
                        )
                      ) : (
                        <ArrowRight size={14} style={{ color: "var(--color-text-placeholder)" }} />
                      )}
                    </button>
                    );
                  })}
                </div>
              ) : null}

              {/* Pending invites accumulated during this compose session */}
              {mode === "offered" && selectedRecipients.some(r => r._isPending) && (
                <div
                  className="rounded-lg"
                  style={{ border: "1px solid var(--color-border-default)", background: "var(--color-surface)" }}
                  data-testid="pending-invites-list"
                >
                  <div className="px-4 py-2 text-xs font-semibold" style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid var(--color-border-default)" }}>
                    Inviting by email
                  </div>
                  {selectedRecipients.filter(r => r._isPending).map((r, i) => (
                    <div key={`${r.email}-${i}`} className="flex items-center gap-3 px-4 py-2.5" style={{ borderBottom: "0.5px solid var(--color-border-default)" }}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)", fontWeight: 600 }}>
                        {(r.display_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: "var(--color-text-primary)" }}>
                          {r.display_name}
                        </div>
                        <div className="text-xs truncate" style={{ color: "var(--color-text-secondary)" }}>
                          {r.email}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedRecipients(prev =>
                          prev.filter(x => !(x._isPending && x.email === r.email)))}
                        className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-slate-100"
                        aria-label="Remove"
                        data-testid={`remove-invite-${r.email}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Send to someone not yet on Evoluras (offer + ask modes) */}
              {(mode === "offered" || mode === "ask") && (
                <button
                  onClick={() => setStep("inviteDetails")}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:opacity-90 transition-opacity"
                  style={{
                    background: "var(--color-brand-ghost)",
                    border: "1px dashed var(--color-border-active)",
                    color: "var(--color-brand-primary)",
                  }}
                  data-testid="invite-non-user"
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                  >
                    <UserPlus size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">
                      {mode === "offered" && selectedRecipients.some(r => r._isPending)
                        ? "Add another email"
                        : "Someone not on Evoluras yet"}
                    </div>
                    <div className="text-xs opacity-80">
                      {mode === "ask"
                        ? "They'll get a link to write their note — then create a free account to save it"
                        : "We'll email them + invite them to join"}
                    </div>
                  </div>
                  <ArrowRight size={14} />
                </button>
              )}

              {/* Continue → compose (offered multi-select only) */}
              {mode === "offered" && selectedRecipients.length > 0 && (
                <Button
                  onClick={() => setStep("compose")}
                  className="w-full rounded-full"
                  style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                  data-testid="pickperson-continue"
                >
                  Continue with {selectedRecipients.length} {selectedRecipients.length === 1 ? "person" : "people"}
                </Button>
              )}
            </div>
          )}

          {/* Invite details (collect name + email of non-user) */}
          {step === "inviteDetails" && (
            <div className="space-y-3">
              <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                They&apos;ll get an email with your gratitude + a link to save it and join your Circle.
              </p>
              <div>
                <Label className="text-xs mb-1.5 block font-semibold" style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Their name
                </Label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="e.g. Marta"
                  maxLength={80}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-2"
                  style={{
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border-default)",
                    color: "var(--color-text-primary)",
                  }}
                  data-testid="invite-name-input"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs mb-1.5 block font-semibold" style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Their email
                </Label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--color-text-placeholder)" }} />
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="marta@example.com"
                    className="w-full pl-9 pr-3 py-2 rounded-lg text-sm outline-none focus:ring-2"
                    style={{
                      background: "var(--color-surface)",
                      border: "1px solid var(--color-border-default)",
                      color: "var(--color-text-primary)",
                    }}
                    data-testid="invite-email-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  onClick={() => setStep("pickPerson")}
                  className="text-sm px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  Back
                </button>
                <Button
                  onClick={() => {
                    const email = inviteEmail.trim().toLowerCase();
                    const name = inviteName.trim();
                    if (!name) return toast.error("Please add their name");
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Please enter a valid email");
                    const invite = { _isPending: true, display_name: name, email };
                    if (mode === "offered") {
                      // Offered-mode: accumulate into the multi-recipient list
                      // and bounce back to pickPerson so the user can add more
                      // or tap Continue.
                      setSelectedRecipients(prev => (
                        prev.some(r => r._isPending && r.email === email)
                          ? prev
                          : [...prev, invite]
                      ));
                      setInviteName("");
                      setInviteEmail("");
                      setStep("pickPerson");
                    } else {
                      // Ask-mode keeps the legacy single-recipient flow.
                      setSelectedConn(invite);
                      setStep("compose");
                    }
                  }}
                  className="rounded-full ml-auto"
                  style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                  data-testid="invite-details-continue"
                >
                  {mode === "offered" ? "Add person" : "Continue"}
                  <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {step === "compose" && (
            <>
              {/* Recipient chips — ask mode keeps single avatar; offered mode
                  shows a wrapped chip row so multi-recipient is visible */}
              {mode === "ask" && selectedConn && (
                <div
                  className="flex items-center gap-3 px-3 py-2 rounded-lg"
                  style={{
                    background: "var(--color-surface-subtle)",
                    border: selectedConn._isPending
                      ? "1px dashed var(--color-border-active)"
                      : "1px solid var(--color-border-default)",
                  }}
                >
                  {selectedConn.display_picture ? (
                    <img src={selectedConn.display_picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{
                        background: "var(--color-brand-ghost)",
                        color: "var(--color-brand-primary)",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {selectedConn._isPending
                        ? <UserPlus size={14} />
                        : (selectedConn.display_name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: "var(--color-text-primary)" }}>
                      {selectedConn.display_name}
                    </div>
                    {selectedConn._isPending && (
                      <div className="text-xs truncate" style={{ color: "var(--color-text-secondary)" }}>
                        {selectedConn.email} · will receive an invite
                      </div>
                    )}
                  </div>
                  {!initialConnectionId && (
                    <button
                      onClick={() => {
                        setSelectedConn(null);
                        setStep(selectedConn._isPending ? "inviteDetails" : "pickPerson");
                      }}
                      className="text-xs hover:underline"
                      style={{ color: "var(--color-text-interactive)" }}
                    >
                      Change
                    </button>
                  )}
                </div>
              )}

              {mode === "offered" && selectedRecipients.length > 0 && (
                <div
                  className="flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-lg"
                  style={{
                    background: "var(--color-surface-subtle)",
                    border: "1px solid var(--color-border-default)",
                  }}
                  data-testid="recipient-chips"
                >
                  <span className="text-xs font-semibold" style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    To:
                  </span>
                  {selectedRecipients.map((r, i) => (
                    <span
                      key={`${r.connection_id || r.email}-${i}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{
                        background: r._isPending ? "var(--color-brand-ghost)" : "var(--color-surface)",
                        color: r._isPending ? "var(--color-brand-primary)" : "var(--color-text-primary)",
                        border: r._isPending ? "1px dashed var(--color-border-active)" : "1px solid var(--color-border-default)",
                      }}
                    >
                      {r._isPending && <UserPlus size={10} />}
                      {r.display_name}
                      <button
                        onClick={() => setSelectedRecipients(prev => prev.filter((_, j) => j !== i))}
                        className="hover:opacity-70"
                        aria-label={`Remove ${r.display_name}`}
                        data-testid={`remove-recipient-${r.connection_id || r.email}`}
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  {!initialConnectionId && (
                    <button
                      onClick={() => setStep("pickPerson")}
                      className="text-xs hover:underline ml-1"
                      style={{ color: "var(--color-text-interactive)" }}
                      data-testid="compose-add-more"
                    >
                      + Add more
                    </button>
                  )}
                </div>
              )}

              {mode === "ask" ? null : (
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="I am grateful…"
                  rows={8}
                  maxLength={1800}
                  className="resize-y min-h-[11rem] text-[15px] leading-relaxed"
                  data-testid="gratitude-content-input"
                  autoFocus
                />
              )}

              {mode !== "ask" && (
                <>
                  {/* Image attachments — up to 4 per card */}
                  <div>
                    <Label className="text-xs mb-2 block font-semibold" style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Images <span className="font-normal normal-case opacity-60">({images.length}/{MAX_IMAGES}, optional)</span>
                    </Label>
                    {images.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-2" data-testid="image-grid">
                        {images.map((src, idx) => (
                          <div
                            key={idx}
                            className="relative rounded-lg overflow-hidden"
                            style={{ border: "1px solid var(--color-border-default)", aspectRatio: "4 / 3" }}
                          >
                            <img
                              src={src}
                              alt={`Attached ${idx + 1}`}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                              data-testid={`image-preview-${idx}`}
                            />
                            <button
                              onClick={() => removeImageAt(idx)}
                              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
                              style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}
                              aria-label="Remove image"
                              data-testid={`image-remove-${idx}`}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {images.length < MAX_IMAGES && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={imageUploading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm transition-colors"
                        style={{
                          background: "var(--color-surface-subtle)",
                          color: "var(--color-text-secondary)",
                          border: "1px dashed var(--color-border-default)",
                        }}
                        data-testid="image-pick"
                      >
                        <ImagePlus size={14} />
                        {imageUploading
                          ? "Processing…"
                          : (images.length === 0 ? "Add a photo" : "Add another photo")}
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleImagePick}
                      data-testid="image-input"
                    />
                  </div>

                  {mode === "self" && (
                    <>
                      <div>
                        <Label className="text-xs mb-2 block font-semibold" style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Link to life areas <span className="font-normal normal-case opacity-60">(optional, pick any)</span>
                        </Label>
                    {wheelAreas.length === 0 ? (
                      <p className="text-xs" style={{ color: "var(--color-text-placeholder)" }}>
                        Build your Wheel of Life first to link gratitudes to areas.
                      </p>
                    ) : (
                      <div className="flex gap-1.5 flex-wrap" data-testid="area-picker">
                        <button
                          onClick={() => setWheelAreaKeys([])}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                          style={{
                            background: wheelAreaKeys.length === 0 ? "var(--color-brand-primary)" : "transparent",
                            color: wheelAreaKeys.length === 0 ? "var(--color-text-on-brand)" : "var(--color-text-secondary)",
                            border: `1px solid ${wheelAreaKeys.length === 0 ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                          }}
                          data-testid="area-none"
                        >
                          {wheelAreaKeys.length === 0 && <Check size={10} />}
                          No area
                        </button>
                        {wheelAreas.map(a => {
                          const active = wheelAreaKeys.includes(a.key);
                          return (
                            <button
                              key={a.key}
                              onClick={() =>
                                setWheelAreaKeys(prev =>
                                  prev.includes(a.key)
                                    ? prev.filter(k => k !== a.key)
                                    : (prev.length >= 5
                                        ? (toast.error("Up to 5 life areas per card"), prev)
                                        : [...prev, a.key])
                                )
                              }
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                              style={{
                                background: active ? a.color : "transparent",
                                color: active ? "#fff" : "var(--color-text-primary)",
                                border: `1px solid ${active ? a.color : "var(--color-border-default)"}`,
                              }}
                              data-testid={`area-${a.key}`}
                              title={a.label}
                            >
                              {active && <Check size={10} />}
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{
                                  background: active ? "#fff" : a.color,
                                  opacity: active ? 0.9 : 1,
                                }}
                              />
                              {a.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Tags — hashtag-style free-form labels. Up to 10. */}
                  <div>
                    <Label className="text-xs mb-2 block font-semibold" style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Tags <span className="font-normal normal-case opacity-60">(optional)</span>
                    </Label>
                    <div
                      className="flex flex-wrap gap-1.5 items-center rounded-lg px-2 py-2"
                      style={{
                        border: "1px solid var(--color-border-default)",
                        background: "var(--color-surface)",
                      }}
                      onClick={(e) => {
                        // Focus the input when the outer box is clicked.
                        const inp = e.currentTarget.querySelector('input[data-testid="tag-input"]');
                        if (inp) inp.focus();
                      }}
                      data-testid="tag-editor"
                    >
                      {tags.map((t, i) => (
                        <span
                          key={`${t}-${i}`}
                          className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            background: "var(--color-brand-ghost)",
                            color: "var(--color-brand-primary)",
                          }}
                          data-testid={`tag-chip-${t.toLowerCase()}`}
                        >
                          #{t}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTags(prev => prev.filter((_, idx) => idx !== i));
                            }}
                            className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-black/10"
                            aria-label={`Remove tag ${t}`}
                            data-testid={`tag-remove-${t.toLowerCase()}`}
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                      <input
                        value={tagDraft}
                        onChange={(e) => setTagDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "," || e.key === " ") {
                            e.preventDefault();
                            const raw = tagDraft.replace(/^#+/, "").trim();
                            if (!raw) return;
                            if (tags.length >= 10) { toast.error("Up to 10 tags per card"); return; }
                            if (raw.length > 30) { toast.error("Tags max 30 characters"); return; }
                            if (tags.some(t => t.toLowerCase() === raw.toLowerCase())) {
                              setTagDraft("");
                              return;
                            }
                            setTags(prev => [...prev, raw]);
                            setTagDraft("");
                          } else if (e.key === "Backspace" && !tagDraft && tags.length) {
                            setTags(prev => prev.slice(0, -1));
                          }
                        }}
                        placeholder={tags.length ? "Add another…" : "e.g. food, uruguay, life"}
                        className="flex-1 min-w-[8rem] bg-transparent outline-none text-sm"
                        style={{ color: "var(--color-text-primary)" }}
                        data-testid="tag-input"
                      />
                    </div>
                    <p className="text-[10px] mt-1" style={{ color: "var(--color-text-placeholder)" }}>
                      Press Enter, comma, or space to add. Up to 10 tags.
                    </p>
                  </div>

                  <div>
                    <Label className="text-xs mb-2 block font-semibold" style={{ color: "var(--color-text-label)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      Who can see this
                    </Label>
                    <div className="flex gap-2 flex-wrap" data-testid="visibility-toggle">
                      {[
                        { value: "private", label: "Keep it to myself", icon: <Lock size={12} /> },
                        { value: "circle", label: "My inner circle", icon: <UsersIcon size={12} /> },
                        { value: "public", label: "Everyone", icon: <Globe size={12} /> },
                      ].map(opt => {
                        const active = visibility === opt.value;
                        return (
                          <button
                            key={opt.value}
                            onClick={() => setVisibility(opt.value)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                            style={{
                              background: active ? "var(--color-brand-primary)" : "transparent",
                              color: active ? "var(--color-text-on-brand)" : "var(--color-text-secondary)",
                              border: `1px solid ${active ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                            }}
                            data-testid={`visibility-toggle-${opt.value}`}
                          >
                            {opt.icon}
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div
          className="flex items-center justify-between gap-3 px-6 py-3 flex-shrink-0"
          style={{ borderTop: "1px solid var(--color-border-default)", background: "var(--color-surface-subtle)" }}
        >
          <Button
            variant="ghost"
            onClick={onClose}
            className="rounded-full h-9 text-sm"
            data-testid="gratitude-dialog-cancel"
          >
            Cancel
          </Button>
          {step === "compose" && (
            <Button
              onClick={handleSubmit}
              disabled={
                submitting ||
                imageUploading ||
                (mode === "ask"
                  ? !selectedConn
                  : (!content.trim() && images.length === 0)) ||
                (mode === "offered" && selectedRecipients.length === 0)
              }
              className="rounded-full h-9 px-5"
              style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
              data-testid="gratitude-dialog-submit"
            >
              {submitting ? "Saving…" : (
                mode === "ask"
                  ? "Send ask"
                  : mode === "offered"
                    ? (selectedRecipients.length > 1 ? `Send to ${selectedRecipients.length}` : "Send note")
                    : "Add gratitude"
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddGratitudeDialog;
